import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {
  cancelBonusQuestion,
  fetchBonusQuestion,
  fetchBonusQuestionGameWeekMatches,
  fetchChampionshipClubs,
  resolveBonusQuestion,
} from '../api';
import Button from '../components/Button';
import ConfirmModal from '../components/ConfirmModal';
import NumberInput from '../components/NumberInput';
import {
  QUESTION_BONUS_LIST_PATH,
  QUESTION_TYPE_SCOPE_LABELS,
  getChampionshipName,
} from '../constants';
import {toFormValues} from '../payload';
import {questionBonusStyles, resolveStyles} from '../styles';
import {
  BonusQuestion,
  BonusQuestionMatch,
  BonusQuestionResolvePayload,
  ChampionshipClubsById,
} from '../types';
import {buildMatchOptions} from '../utils';

interface ResolveQuestionBonusProps {
  history?: {push: (path: string) => void};
  match?: {params: {bonusQuestionId?: string}};
}

const SummaryField = ({label, value}: {label: string; value: string}) => (
  <div style={resolveStyles.summaryField}>
    <span style={resolveStyles.summaryLabel}>{label}</span>
    <span style={resolveStyles.summaryValue}>{value}</span>
  </div>
);

export const ResolveQuestionBonus = ({
  history,
  match,
}: ResolveQuestionBonusProps) => {
  const dispatch = useDispatch();
  const bonusQuestionId = match?.params?.bonusQuestionId;

  const [bonusQuestion, setBonusQuestion] = useState<BonusQuestion | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);

  const [winningOptionIndex, setWinningOptionIndex] = useState<
    number | undefined
  >(undefined);
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');

  const [matches, setMatches] = useState<BonusQuestionMatch[]>([]);
  const [clubsById, setClubsById] = useState<ChampionshipClubsById>({});

  const [isConfirmingResolve, setIsConfirmingResolve] = useState(false);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!bonusQuestionId) return;
    setIsLoading(true);
    dispatch(
      fetchBonusQuestion(
        bonusQuestionId,
        fetchedQuestion => {
          setBonusQuestion(fetchedQuestion);
          setIsLoading(false);
        },
        () => {
          setLoadError('Le chargement de la question a échoué.');
          setIsLoading(false);
        },
      ),
    );
  }, [dispatch, bonusQuestionId]);

  const isMatchForecast = bonusQuestion?.questionType === 'matchForecast';

  // Le libellé du match n'est pas porté par la question : on le reconstruit
  // depuis les matchs de la journée et le référentiel clubs.
  useEffect(() => {
    if (!isMatchForecast || !bonusQuestion?.gameWeekNumber) return;
    dispatch(fetchChampionshipClubs(setClubsById, () => setClubsById({})));
    dispatch(
      fetchBonusQuestionGameWeekMatches(
        bonusQuestion.championshipId,
        bonusQuestion.gameWeekNumber,
        setMatches,
        () => setMatches([]),
      ),
    );
  }, [
    dispatch,
    isMatchForecast,
    bonusQuestion?.championshipId,
    bonusQuestion?.gameWeekNumber,
  ]);

  const matchLabel = useMemo(() => {
    const championshipMatchId = bonusQuestion?.matchForecast?.championshipMatchId;
    if (!championshipMatchId) return '-';
    const option = buildMatchOptions(matches, clubsById).find(
      matchOption => matchOption.value === championshipMatchId,
    );
    if (!option) return championshipMatchId;
    return option.hint ? `[${option.hint}] ${option.label}` : option.label;
  }, [bonusQuestion, matches, clubsById]);

  const quizOptions = useMemo(
    () => (bonusQuestion ? toFormValues(bonusQuestion).answers.quiz.options : []),
    [bonusQuestion],
  );

  const canResolve = isMatchForecast
    ? homeScore !== '' && awayScore !== ''
    : winningOptionIndex !== undefined;

  const goToList = () => {
    if (history) history.push(QUESTION_BONUS_LIST_PATH);
  };

  const buildResolvePayload = (): BonusQuestionResolvePayload =>
    isMatchForecast
      ? {homeScore: parseInt(homeScore, 10), awayScore: parseInt(awayScore, 10)}
      : {winningOptionIndex: winningOptionIndex || 0};

  const confirmResolve = () => {
    if (!bonusQuestionId) return;
    setIsSubmitting(true);
    setSubmitError(undefined);
    dispatch(
      resolveBonusQuestion(
        bonusQuestionId,
        buildResolvePayload(),
        () => {
          setIsSubmitting(false);
          setIsConfirmingResolve(false);
          goToList();
        },
        () => {
          setIsSubmitting(false);
          setIsConfirmingResolve(false);
          setSubmitError('La validation de la réponse a échoué.');
        },
      ),
    );
  };

  /** Même action que "Supprimer" dans la liste. */
  const confirmCancel = () => {
    if (!bonusQuestionId) return;
    setIsSubmitting(true);
    setSubmitError(undefined);
    dispatch(
      cancelBonusQuestion(
        bonusQuestionId,
        () => {
          setIsSubmitting(false);
          setIsConfirmingCancel(false);
          goToList();
        },
        () => {
          setIsSubmitting(false);
          setIsConfirmingCancel(false);
          setSubmitError("L'annulation de la question a échoué.");
        },
      ),
    );
  };

  if (isLoading) {
    return (
      <div style={questionBonusStyles.page}>
        <p style={questionBonusStyles.emptyState}>Chargement de la question…</p>
      </div>
    );
  }

  if (loadError || !bonusQuestion) {
    return (
      <div style={questionBonusStyles.page}>
        <p style={questionBonusStyles.errorMessage}>
          {loadError || 'Question introuvable.'}
        </p>
      </div>
    );
  }

  return (
    <div style={questionBonusStyles.page}>
      <h1 style={questionBonusStyles.pageTitle}>
        <button
          type="button"
          title="Revenir à la liste"
          aria-label="Revenir à la liste"
          onClick={goToList}
          style={resolveStyles.backLink}>
          <i className="fa fa-angle-left" />
        </button>
        Réponse {getChampionshipName(bonusQuestion.championshipId)} - GW{' '}
        {bonusQuestion.gameWeekNumber}
        <span style={questionBonusStyles.pageTitleSuffix}>MPP</span>
      </h1>

      <div style={resolveStyles.card}>
        <div style={resolveStyles.summaryRow}>
          <SummaryField
            label="Compétition"
            value={getChampionshipName(bonusQuestion.championshipId)}
          />
          <SummaryField
            label="Type"
            value={QUESTION_TYPE_SCOPE_LABELS[bonusQuestion.questionType]}
          />
          {isMatchForecast && <SummaryField label="Match" value={matchLabel} />}
        </div>

        <div style={resolveStyles.summaryField}>
          <span style={resolveStyles.summaryLabel}>Question :</span>
          <h2 style={resolveStyles.questionTitle}>
            {bonusQuestion.title?.['fr-FR'] || '-'}
          </h2>
        </div>

        <div style={resolveStyles.summaryField}>
          <span style={resolveStyles.answerLabel}>Bonne réponse :</span>

          {isMatchForecast ? (
            <div style={resolveStyles.scoreRow}>
              <NumberInput
                width={100}
                value={homeScore}
                onChange={setHomeScore}
              />
              <span style={resolveStyles.scoreSeparator}>:</span>
              <NumberInput
                width={100}
                value={awayScore}
                onChange={setAwayScore}
              />
            </div>
          ) : (
            <div style={resolveStyles.optionsList} role="radiogroup">
              {quizOptions.map((option, index) => {
                const isSelected = winningOptionIndex === index;
                return (
                  <button
                    key={option.key}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setWinningOptionIndex(index)}
                    style={{
                      ...resolveStyles.option,
                      ...(isSelected ? resolveStyles.optionSelected : {}),
                    }}>
                    <span style={resolveStyles.radio}>
                      {isSelected && <span style={resolveStyles.radioDot} />}
                    </span>
                    <span style={resolveStyles.optionText}>
                      {option.label.fr}
                    </span>
                    <span style={resolveStyles.optionPoints}>
                      {option.points} pts
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {submitError && (
          <p style={questionBonusStyles.errorMessage}>{submitError}</p>
        )}

        <div style={questionBonusStyles.footer}>
          <Button
            disabled={!canResolve || isSubmitting}
            onClick={() => setIsConfirmingResolve(true)}>
            Valider la bonne réponse
          </Button>
          <Button
            variant="dangerOutline"
            disabled={isSubmitting}
            onClick={() => setIsConfirmingCancel(true)}>
            Annuler question
          </Button>
        </div>
      </div>

      {isConfirmingResolve && (
        <ConfirmModal
          variant="danger"
          confirmVariant="primary"
          title="Etes-vous sûr ?"
          description="Une fois la réponse validée, les points seront distribués aux utilisateurs ayant sélectionné la réponse"
          cancelLabel="Choisir une autre réponse"
          confirmLabel="Valider"
          isConfirming={isSubmitting}
          onConfirm={confirmResolve}
          onCancel={() => setIsConfirmingResolve(false)}
        />
      )}

      {isConfirmingCancel && (
        <ConfirmModal
          variant="danger"
          title="Etes-vous sûr ?"
          description="Une fois la question annulée, la card de la question passera au statut correspondant sur l'app et aucun utilisateur ne gagnera de point"
          cancelLabel="Revenir en arrière"
          confirmLabel="Annuler la question"
          isConfirming={isSubmitting}
          onConfirm={confirmCancel}
          onCancel={() => setIsConfirmingCancel(false)}
        />
      )}
    </div>
  );
};

export default ResolveQuestionBonus;



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/QuestionBonus/Resolve/index.tsx